/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */

import cx from "classnames";
import React, { useCallback, useMemo } from "react";

import { usePlayerComponents } from "../../../../../../shared/context/PlayerComponentsContext";
import { usePlayerTranslation } from "../../../../../../shared/context/PlayerTranslationContext";
import LiveIcon from "../../../../../../shared/icons/player/Live.svg";
import { formatCurrentTime } from "../../../../../../shared/utils/time";
import {
  usePlayerController,
  usePlayerSettings,
  usePlayerTimer,
} from "../../../../../BasePlayer";

import "./LiveButton.scss";

export const LiveButton = () => {
  const {
    timer: { currentTime, duration, atLiveEdge, isLive, liveDuration },
  } = usePlayerTimer();

  const {
    ui: { tooltipOffset },
  } = usePlayerSettings();

  const { t } = usePlayerTranslation();
  const { Button } = usePlayerComponents();

  const { player } = usePlayerController();

  const show = Boolean(currentTime && duration === Infinity);

  const onSeekToLive = useCallback(() => player.seekToLiveEdge(), [player]);

  const secondsBehind = useMemo(() => {
    if (isLive && !atLiveEdge) {
      const secondsBehind = liveDuration - currentTime;

      return (secondsBehind < 1 ? "" : "-") + formatCurrentTime(secondsBehind);
    }

    return "";
  }, [atLiveEdge, currentTime, isLive, liveDuration]);

  const buttonClasses = cx("LiveButton", {
    ["on"]: atLiveEdge,
    ["off"]: !atLiveEdge,
  });

  if (!show) {
    return <></>;
  }

  return (
    <div className="LiveButtonWrapper">
      <Button
        variant="tertiary"
        className={buttonClasses}
        onClick={onSeekToLive}
        tooltip={t(
          atLiveEdge
            ? "PLAYER_TOOLTIP_CURRENTLY_LIVE"
            : "PLAYER_TOOLTIP_GO_TO_LIVE",
        )}
        tooltipOffset={tooltipOffset}
      >
        <LiveIcon aria-hidden="true" focusable="false" />
        Live
      </Button>
      {secondsBehind}
    </div>
  );
};
